import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const NAV = [
  {
    label: "Articles",
    href: "/writing",
    subs: [
      { label: "FMCG", href: "/writing?cat=FMCG" },
      { label: "D2C", href: "/writing?cat=D2C" },
      { label: "Marketplace", href: "/writing?cat=Marketplace" },
      { label: "Consumer Tech", href: "/writing?cat=Consumer+Tech" },
    ],
  },
  {
    label: "Case Studies",
    href: "/case-studies",
    subs: [
      { label: "FMCG", href: "/case-studies?cat=FMCG" },
      { label: "D2C", href: "/case-studies?cat=D2C" },
      { label: "Consumer Tech", href: "/case-studies?cat=Consumer+Tech" },
    ],
  },
  { label: "About", href: "/about" },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setOpenMenu(null); }, [location]);

  useEffect(() => {
    const handleClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setOpenMenu(null);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const isActive = (href) => {
    const base = href.split("?")[0];
    return location.pathname === base || location.pathname.startsWith(base + "/");
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-[#E5E7EB] shadow-sm"
          : "bg-white/80 backdrop-blur-xl border-b border-white/40"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex flex-col leading-none group flex-shrink-0" data-testid="header-logo">
          <span className="font-['Outfit'] text-lg font-semibold text-[#0A0A0A] tracking-tight group-hover:text-[#002FA7] transition-colors">
            Niharika Patel
          </span>
          <span className="text-[9px] tracking-[0.18em] uppercase text-[#6B7280] font-medium">
            Growth & Marketing
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav ref={navRef} className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
          {NAV.map((link) => (
            <div key={link.href} className="relative">
              {link.subs ? (
                <>
                  <button
                    onClick={() => setOpenMenu(openMenu === link.label ? null : link.label)}
                    className={`flex items-center gap-1 text-sm font-medium px-3 py-2 transition-colors ${
                      isActive(link.href) ? "text-[#002FA7]" : "text-[#4B5563] hover:text-[#0A0A0A]"
                    }`}
                    data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {link.label}
                    <ChevronDown size={12} className={`transition-transform duration-200 ${openMenu === link.label ? "rotate-180" : ""}`} />
                  </button>
                  {openMenu === link.label && (
                    <div className="absolute top-full left-0 mt-1 w-44 bg-white border border-[#E5E7EB] shadow-lg z-50">
                      <div className="py-1">
                        <Link
                          to={link.href}
                          className="block px-4 py-2 text-xs font-bold uppercase tracking-wide text-[#002FA7] hover:bg-[#F9FAFB] border-b border-[#F3F4F6]"
                          onClick={() => setOpenMenu(null)}
                        >
                          All {link.label}
                        </Link>
                        {link.subs.map((sub) => (
                          <Link
                            key={sub.href}
                            to={sub.href}
                            className="block px-4 py-2.5 text-sm text-[#4B5563] hover:bg-[#F9FAFB] hover:text-[#002FA7] transition-colors"
                            onClick={() => setOpenMenu(null)}
                          >
                            {sub.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={link.href}
                  className={`text-sm font-medium px-3 py-2 block transition-colors ${
                    isActive(link.href) ? "text-[#002FA7]" : "text-[#4B5563] hover:text-[#0A0A0A]"
                  }`}
                >
                  {link.label}
                </Link>
              )}
            </div>
          ))}
          <Link
            to="/book"
            data-testid="header-book-btn"
            className="ml-3 text-sm font-semibold bg-[#002FA7] text-white px-5 py-2 hover:bg-[#001D66] transition-colors"
          >
            Work With Me
          </Link>
        </nav>

        {/* Mobile Toggle */}
        <button
          className="lg:hidden p-2 text-[#0A0A0A]"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-btn"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-[#E5E7EB] px-6 py-4 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
          {NAV.map((link) => (
            <div key={link.href}>
              <Link to={link.href} className="block text-[#0A0A0A] font-medium py-2.5 border-b border-[#F3F4F6]">
                {link.label}
              </Link>
              {link.subs && (
                <div className="pl-4 flex flex-col gap-0.5 mt-1 mb-2">
                  {link.subs.map((sub) => (
                    <Link key={sub.href} to={sub.href} className="text-sm text-[#4B5563] py-1.5">
                      {sub.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <Link to="/book" className="bg-[#002FA7] text-white text-center py-3 font-semibold mt-3">
            Work With Me
          </Link>
        </div>
      )}
    </header>
  );
}
